import { colors } from 'components/styles/variables';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { ReactNode } from 'react';
import { EngagementTypes } from 'lib/Tracking/NavBarClick';
import closeButton from './close-button.png';
import { useLog } from '../Desktop/useNavBarClickLog';

interface ListLinkProps {
  href: string;
  children?: ReactNode;
  sectionName: string;
  currentItem?: string;
  engagementType: EngagementTypes;
}

const itemState = (itemName: string, currentItem?: string): string =>
  itemName === currentItem ? 'active' : '';

const ListLink = ({
  href,
  children,
  sectionName,
  engagementType,
  currentItem,
  ...passThrough
}: ListLinkProps): JSX.Element => {
  const log = useLog();

  return (
    <li className={itemState(sectionName, currentItem)}>
      <Link href={href}>
        <a
          target="_top"
          href={href}
          onClick={(): void => {
            log(engagementType);
          }}
          {...passThrough}
        >
          {children}
        </a>
      </Link>

      <style jsx>{`
        li {
          height: 56px;
          font-size: 14px;
          padding-left: 40px;
          line-height: 60px;
        }

        li.active {
          border-left-color: ${colors.ALR1};
          border-left-style: solid;
          border-left-width: 5px;
          padding-left: 35px;
        }

        a {
          color: black;
          font-size: 16px;
        }

        a:hover {
          text-decoration: none;
        }
      `}</style>
    </li>
  );
};

interface DropdownProps {
  show: boolean;
  close: () => void;
  currentItem?: string;
}

function Dropdown({ show, close, currentItem }: DropdownProps): JSX.Element {
  const { t } = useTranslation();
  const log = useLog();

  return (
    <nav className={`${show ? '' : 'hidden'}`}>
      <div className="close-container">
        <button className="close" onClick={close} aria-label="Close menu" />
      </div>
      <ul>
        <ListLink engagementType="home_tap" currentItem={currentItem} href="/" sectionName="home">
          {t('header.home')}
        </ListLink>
        <ListLink
          engagementType="saved_searches_tap"
          currentItem={currentItem}
          href="/user/saved-searches"
          sectionName="savedSearches"
        >
          {t('header.savedSearches')}
        </ListLink>
        <ListLink
          currentItem={currentItem}
          engagementType="favorites_tap"
          href="/user/favorites"
          sectionName="favorites"
        >
          {t('header.favorites')}
        </ListLink>
        <ListLink
          currentItem={currentItem}
          engagementType="guides_and_reviews_tap"
          href={'/guides'}
          sectionName="guidesAndReviews"
        >
          {t('header.guidesAndReviews')}
        </ListLink>
        <ListLink
          currentItem={currentItem}
          engagementType="news_and_analysis_tap"
          href={'/news-and-analysis'}
          sectionName="newsAndAnalysis"
        >
          {t('header.newsAndAnalysis')}
        </ListLink>
        <ListLink
          engagementType="sell_my_car_tap"
          currentItem={currentItem}
          href="https://www.cargurus.com/sell-car/?pid=AutoList"
          sectionName="sell"
        >
          {t('header.sellMyCar')}
        </ListLink>
        <ListLink
          engagementType="car_values_tap"
          currentItem={currentItem}
          href="/prices"
          sectionName="carValues"
        >
          {t('header.carValues')}
        </ListLink>
        <ListLink
          engagementType="settings_tap"
          currentItem={currentItem}
          href="/user/settings"
          sectionName="settings"
        >
          {t('header.settings')}
        </ListLink>
      </ul>
      <a
        target="_top"
        className="download-app-button"
        onClick={(): void => {
          log('app_download_tap');
        }}
        href="https://autolist.com/download?utm_source=app_interstitial&utm_medium=mobile_web"
      >
        {'Get the App'}
      </a>
      <style jsx>
        {`
          nav {
            background-color: white;
            z-index: 1000;
            position: fixed;
            top: 0px;
            right: -10px;
            width: 275px;
            padding-right: 30px;
            height: 100vh;
          }

          ul {
            padding-left: 0px;
            list-style-type: none;
            margin-top: 0px;
          }

          .close-container {
            display: flex;
            align-items: flex-end;
            flex-direction: column;
          }

          .close {
            height: 20px;
            width: 20px;
            background-image: url('${closeButton}');
            background-size: 20px 20px;
            background-color: transparent;
            background-repeat: no-repeat;
            border: none;
            margin-top: 20px;
            margin-bottom: 20px;
          }

          .download-app-button {
            display: block;
            height: 40px;
            width: 150px;
            margin-left: 37px;
            padding: 10px 0px;
            color: white;
            background-color: ${colors.ALR1};
            text-transform: uppercase;
            border-radius: 4px;
            margin-top: 20px;
            font-size: 15px;
            letter-spacing: 0.2px;
            font-weight: 600;
            text-align: center;
          }

          .download-app-button:focus {
            text-decoration: none;
          }
        `}
      </style>
    </nav>
  );
}

export default Dropdown;
